/* ──────────────────────────────────────────────────────────────
   utils/programImportExport.ts       –  ExcelJS version
   ────────────────────────────────────────────────────────────── */

import ExcelJS from 'exceljs';
import {
  IFarmInputProduct,
  IFarmInputProgramLineInput,
} from 'constants/interfaces';
import {
  PROGRAM_STAGES,
  PROGRAM_APPLICATION_UNIT,
  PROGRAM_APPLICATION_TYPE,
} from 'constants/index';

const MAIN_SHEET = 'Lines';
const LIST_SHEET = 'Lists';
const LAST_ROW = 1_000; // rows left open for the user

/* helper – wrap sheet names in single-quotes for formulas */
const q = (s: string) => `'${s.replace(/'/g, "''")}'`;

/* ══════════════════════════════════════════════════════════════
   1. Build Excel template
   ══════════════════════════════════════════════════════════════ */
export async function buildProgramTemplate(
  products: IFarmInputProduct[],
): Promise<Blob> {
  const wb = new ExcelJS.Workbook();

  /* ---------- List sheet (VERY-hidden) ---------- */
  const wsLists = wb.addWorksheet(LIST_SHEET, {state: 'veryHidden'});

  // A-C → product lookup  (A:Name  B:ID  C:Price)
  wsLists.addRow(['Name', 'ID', 'Price']);
  products.forEach((p) => wsLists.addRow([p.name, p.id, p.price]));
  const prodLastRow = products.length + 1;

  // Column E – stages   |  F – units   |  G – types
  wsLists.getColumn('E').values = ['Stage', ...PROGRAM_STAGES];
  wsLists.getColumn('F').values = ['Unit', ...PROGRAM_APPLICATION_UNIT];
  wsLists.getColumn('G').values = ['Type', ...PROGRAM_APPLICATION_TYPE];

  /* ---------- Main sheet ---------- */
  const ws = wb.addWorksheet(MAIN_SHEET);

  const header = [
    'Stage', // A
    'Product', // B  (dropdown of names)
    'Price (R/unit)', // C  (auto – formula)
    'Application Rate', // D
    'Application Unit', // E
    'Application Type', // F
    'Application Type / Ha', // G
    'Quantity', // H
    'Comment', // I
  ];
  ws.addRow(header);
  ws.views = [{state: 'frozen', ySplit: 1}];
  header.forEach((_, i) => (ws.getColumn(i + 1).width = 18));

  /* helper – add list-validation to a column                         */
  function addDV(col: number, listRange: string) {
    const colLetter = ws.getColumn(col).letter;
    (ws as any).dataValidations.add(`${colLetter}2:${colLetter}${LAST_ROW}`, {
      type: 'list',
      allowBlank: true,
      formulae: [listRange],
    });
  }

  /* ---- dropdowns -------------------------------------------------- */
  addDV(1, `=${q(LIST_SHEET)}!$E$2:$E$${PROGRAM_STAGES.length + 1}`); // Stage
  addDV(2, `=${q(LIST_SHEET)}!$A$2:$A$${prodLastRow}`); // Product
  addDV(5, `=${q(LIST_SHEET)}!$F$2:$F$${PROGRAM_APPLICATION_UNIT.length + 1}`);
  addDV(6, `=${q(LIST_SHEET)}!$G$2:$G$${PROGRAM_APPLICATION_TYPE.length + 1}`);

  /* ---- Price column – now uses VLOOKUP (works everywhere) --------- */
  for (let r = 2; r <= LAST_ROW; r += 1) {
    const priceCell = ws.getCell(r, 3); // column C
    priceCell.value = {
      formula: `IFERROR(VLOOKUP($B${r},${q(
        LIST_SHEET,
      )}!$A$2:$C$${prodLastRow},3,FALSE),"")`,
    };
  }

  /* ---- export ----------------------------------------------------- */
  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/* ══════════════════════════════════════════════════════════════
   2. Parse uploaded template
   ══════════════════════════════════════════════════════════════ */
export async function parseProgramTemplate(
  arrayBuf: ArrayBuffer,
  products: IFarmInputProduct[],
): Promise<IFarmInputProgramLineInput[]> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuf);

  const ws = wb.getWorksheet(MAIN_SHEET);
  if (!ws) throw new Error('Lines sheet not found');

  const lines: IFarmInputProgramLineInput[] = [];

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const v = (row.values ?? []) as any[]; // 1-based
    const [
      ,
      // 0
      stage, // A
      productName, // B // C – formula, ignored
      ,
      /* price */ applicationRate, // D
      applicationUnit, // E
      applicationType, // F
      applicationTypePerHa, // G
      quantity, // H
      comment, // I
    ] = v;

    if (!productName) return; // blank row

    /* ----- validations -------------------------------------------- */
    if (!PROGRAM_STAGES.includes(stage))
      throw new Error(`Row ${rowNumber}: invalid Stage "${stage}"`);

    if (!PROGRAM_APPLICATION_UNIT.includes(applicationUnit))
      throw new Error(`Row ${rowNumber}: invalid Unit "${applicationUnit}"`);

    if (!PROGRAM_APPLICATION_TYPE.includes(applicationType))
      throw new Error(`Row ${rowNumber}: invalid Type "${applicationType}"`);

    const prod = products.find((p) => p.name === productName);
    if (!prod)
      throw new Error(`Row ${rowNumber}: unknown Product "${productName}"`);

    lines.push({
      productId: String(prod.id),
      when: stage,
      applicationRate: Number(applicationRate),
      applicationUnit,
      applicationType,
      applicationTypePerHectare:
        applicationType === 'Per Hectare' ? 1 : Number(applicationTypePerHa),
      quantity: Number(quantity),
      PHI: 0,
      comment: String(comment || ''),
    });
  });

  return lines;
}
